import { useMatchLobbyRenderControllerState } from '@plvs/respawn/renderController'
import {
  CurrentGameAssistantStateInput,
  Exact,
  GameAssistantStateFragment,
  GameAssistantStepUpdatedDocument,
  GameAssistantStepUpdatedSubscription,
  GameAssistantStepUpdatedSubscriptionVariables,
  GetCurrentGameAssistantStepByMatchIdQuery,
  useGetCurrentGameAssistantStepByMatchIdQuery,
} from '@plvs/graphql'
import { defaultToEmptyString, useRefreshSubscription } from '@plvs/utils'
import { useCallback, useEffect, useState } from 'react'
import { ApolloQueryResult } from '@apollo/client'
import { noop } from 'ramda-adjunct'
import { useWhyDidYouUpdate } from '@plvs/rally/logging'

export type OnNewStepProps = {
  prevState: { stepOrdinal: number | undefined; stepId: string | undefined }
  newState: { stepOrdinal: number | undefined }
}

export type MatchAssistantSubscriptionProps = {
  matchId: string
  teamId: string
  onNewStep({ prevState, newState }: OnNewStepProps): void
  onSkipMatchAssistant?(): void
}

export type MatchAssistantSubscriptionReturn = {
  currentStepLoading: boolean
  prevStepId: string
  prevStepData: GameAssistantStateFragment | null | undefined
  currentStep: GameAssistantStateFragment | null | undefined
  refetchCurrentGameAssistantStepByMatchId: (
    variables?:
      | Partial<Exact<{ input: CurrentGameAssistantStateInput }>>
      | undefined
  ) => Promise<ApolloQueryResult<GetCurrentGameAssistantStepByMatchIdQuery>>
}

export function useMatchAssistantSubscription({
  matchId,
  teamId,
  onSkipMatchAssistant,
  onNewStep,
}: MatchAssistantSubscriptionProps): MatchAssistantSubscriptionReturn {
  const [prevStepId, setPrevStepId] = useState('')
  const [unsubscribeFn, setUnsubscribeFn] = useState<() => void>()

  const {
    getMatchLobbyRenderControllerState,
  } = useMatchLobbyRenderControllerState()
  const { match: matchAssistantActions } = getMatchLobbyRenderControllerState()

  const canParticipateOrSpectate =
    matchAssistantActions.canParticipate || matchAssistantActions.canSpectate
  const shouldSkipSubscription = !matchId || !canParticipateOrSpectate

  const onSkipMatchAssistantCallback = useCallback(() => {
    onSkipMatchAssistant?.()
  }, [])
  const onNewStepCallback = useCallback((props) => {
    onNewStep?.(props)
  }, [])

  const {
    data: currentStepData,
    loading: currentStepLoading,
    refetch: refetchCurrentGameAssistantStepByMatchId,
    subscribeToMore,
    previousData: prevStepData,
  } = useGetCurrentGameAssistantStepByMatchIdQuery({
    variables: {
      input: {
        matchId: defaultToEmptyString(matchId),
        teamId: defaultToEmptyString(teamId),
      },
    },
    skip: shouldSkipSubscription,
  })

  const refetch = ({
    matchId: incomingMatchId,
    teamId: incomingTeamId,
  }: {
    matchId: string | null
    teamId: string | null
  }): void => {
    if (shouldSkipSubscription) {
      return
    }

    refetchCurrentGameAssistantStepByMatchId({
      input: {
        matchId: defaultToEmptyString(incomingMatchId),
        teamId: defaultToEmptyString(incomingTeamId),
      },
    })
  }

  function initializeSubscription(): () => void {
    if (!teamId) {
      return noop
    }

    return subscribeToMore<
      GameAssistantStepUpdatedSubscription,
      GameAssistantStepUpdatedSubscriptionVariables
    >({
      document: GameAssistantStepUpdatedDocument,
      variables: {
        input: {
          teamId,
          matchId: defaultToEmptyString(matchId),
          lastStepIdSeenByTeam:
            currentStepData?.currentGameAssistantStateByMatchId.step?.id,
        },
      },
      updateQuery: (prev, { subscriptionData }) => {
        const newState =
          subscriptionData?.data?.gameAssistantStepUpdated?.currentState
        const prevState = prev.currentGameAssistantStateByMatchId

        if (!newState?.showMatchAssistant) {
          onSkipMatchAssistant?.()
          // Returning prev only for typing purposes.
          return prev
        }

        if (newState.step?.stepOrdinal !== prevState.step?.stepOrdinal) {
          const p = {
            stepOrdinal: prevState.step?.stepOrdinal,
            stepId: prevState.step?.id,
          }
          const n = { stepOrdinal: newState.step?.stepOrdinal }
          onNewStep({ prevState: p, newState: n })
          setPrevStepId(defaultToEmptyString(p.stepId))
        }

        return {
          ...prev,
          currentGameAssistantStateByMatchId: newState,
        }
      },
    })
  }

  useEffect(() => {
    initializeSubscription()
  }, [subscribeToMore, teamId, matchId])

  useEffect(
    function refetchOnMatchOrTeamChange() {
      refetch({ matchId, teamId })
    },
    [teamId, matchId]
  )

  const subscribeProps = {
    teamId,
    unsubscribe: unsubscribeFn,
    onUnsubscribe: setUnsubscribeFn,
    subscribe: subscribeToMore,
    matchId,
    onSkipMatchAssistant: onSkipMatchAssistantCallback,
    onNewStep: onNewStepCallback,
    handlePrevStep: setPrevStepId,
  }

  useRefreshSubscription({
    onVisible: useCallback(
      function refetchAndResubscribe() {
        initializeSubscription()
      },
      [...Object.values(subscribeProps)]
    ),
  })

  useWhyDidYouUpdate('useMatchAssistantSubscription', {
    currentStepLoading,
    prevStepId,
    prevStepData: prevStepData?.currentGameAssistantStateByMatchId,
    currentStep: currentStepData?.currentGameAssistantStateByMatchId,
    refetchCurrentGameAssistantStepByMatchId,
    ...subscribeProps,
  })

  return {
    currentStepLoading,
    prevStepId,
    prevStepData: prevStepData?.currentGameAssistantStateByMatchId,
    currentStep: currentStepData?.currentGameAssistantStateByMatchId,
    refetchCurrentGameAssistantStepByMatchId,
  }
}
